import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import SignatureCanvas from 'react-signature-canvas'
import { X, RotateCcw, Check } from 'lucide-react'
import api from '../../services/api'

/**
 * Work Order Sign Dialog
 * Allows end-client to sign with finger on tablet
 * Generates PDF with signature + timestamp
 */
function WorkOrderSignDialog({ isOpen, onClose, workOrderId, projectId }) {
  const { t } = useTranslation()
  const [signatureName, setSignatureName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const sigCanvasRef = useRef(null)

  useEffect(() => {
    if (isOpen && sigCanvasRef.current) {
      sigCanvasRef.current.clear()
      setSignatureName('')
    }
  }, [isOpen])

  const handleClear = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear()
    }
  }

  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    setError(null)
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setError(t('mobile.signature.required', { defaultValue: 'Handtekening vereist' }))
      return
    }
    if (!signatureName.trim()) {
      setError(t('mobile.signature.name_required', { defaultValue: 'Naam vereist' }))
      return
    }

    setIsSubmitting(true)
    try {
      const signatureDataUrl = sigCanvasRef.current.toDataURL('image/png')
      await api.post(`/mobile/worker/jobs/${workOrderId}/sign`, {
        signature_data: signatureDataUrl,
        signer_name: signatureName.trim(),
        signed_at: new Date().toISOString(),
      })
      onClose()
    } catch {
      setError(t('mobile.signature.error', { defaultValue: 'Ondertekening mislukt. Probeer opnieuw.' }))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('mobile.signature.title', { defaultValue: 'Onderteken Werkbon' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Instructions */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('mobile.signature.instructions', { defaultValue: 'Teken hieronder met uw vinger of stylus' })}
          </p>

          {/* Signature Canvas */}
          <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white">
            <SignatureCanvas
              ref={sigCanvasRef}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas w-full h-48'
              }}
              backgroundColor="white"
              penColor="black"
            />
          </div>

          {/* Clear Button */}
          <button
            onClick={handleClear}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            <RotateCcw className="w-4 h-4" />
            {t('mobile.signature.clear', { defaultValue: 'Wissen' })}
          </button>

          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('mobile.signature.name', { defaultValue: 'Naam' })}
            </label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder={t('mobile.signature.name_placeholder', { defaultValue: 'Volledige naam' })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !signatureName.trim()}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="w-4 h-4" />
            {isSubmitting
              ? t('mobile.signature.submitting', { defaultValue: 'Verzenden...' })
              : t('mobile.signature.submit', { defaultValue: 'Ondertekenen' })
            }
          </button>
        </div>
      </div>
    </div>
  )
}

export default WorkOrderSignDialog
